import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, sendPasswordResetEmail } from 'firebase/auth';
import { auth, googleProvider } from '../../firebase/config';
import { 
  FaEnvelope, 
  FaLock, 
  FaSignInAlt, 
  FaGoogle, 
  FaExclamationCircle,
  FaCheckCircle,
  FaArrowRight,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';
import Navbar from '../layout/Navbar';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        default:
          setError('Failed to sign in. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      await signInWithPopup(auth, googleProvider);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Google sign in error:', error);
      setError('Failed to sign in with Google. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address to reset password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, formData.email);
      setResetEmailSent(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Failed to send reset email. Please check your email address.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="auth-wrapper">
        <div className="auth-container">
          {/* Left Side - Welcome Message */}
          <div className="auth-side">
            <div className="auth-side-content">
              <div className="auth-logo">
                <div className="logo-circle">
                  <FaSignInAlt />
                </div>
                <h1>Tutor Connect</h1>
                <span className="logo-subtitle">Cameroon</span>
              </div>
              
              <div className="auth-features">
                <h2>Welcome Back</h2>
                <p>Sign in to continue your learning journey</p>
                
                <div className="features-list">
                  <div className="feature">
                    <div className="feature-icon">
                      <FaCheckCircle />
                    </div>
                    <div className="feature-text">
                      <h4>Access Your Dashboard</h4>
                      <p>View your bookings, messages, and progress</p>
                    </div>
                  </div>
                  
                  <div className="feature">
                    <div className="feature-icon">
                      <FaCheckCircle />
                    </div>
                    <div className="feature-text">
                      <h4>Find Qualified Tutors</h4>
                      <p>Connect with verified tutors in Cameroon</p>
                    </div>
                  </div>
                  
                  <div className="feature">
                    <div className="feature-icon">
                      <FaCheckCircle />
                    </div>
                    <div className="feature-text">
                      <h4>Manage Sessions</h4>
                      <p>Schedule and track your tutoring sessions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="auth-main">
            <div className="auth-card">
              <div className="auth-header">
                <h2>Sign In to Your Account</h2>
                <p>Enter your credentials to access your dashboard</p>
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="message error-message">
                  <FaExclamationCircle className="message-icon" />
                  <div className="message-content">
                    <h4>Authentication Error</h4>
                    <p>{error}</p>
                  </div>
                </div>
              )}
              
              {/* Success Message */}
              {resetEmailSent && (
                <div className="message success-message">
                  <FaCheckCircle className="message-icon" />
                  <div className="message-content">
                    <h4>Check Your Email</h4>
                    <p>Password reset instructions have been sent to your email address.</p>
                  </div>
                </div>
              )}
              
              {/* Login Form */}
              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <div className="input-group">
                    <FaEnvelope className="input-icon" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="Email address"
                      required
                      disabled={loading}
                      className="form-input"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <div className="input-group">
                    <FaLock className="input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Password"
                      required
                      disabled={loading}
                      className="form-input"
                    />
                    <button 
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                
                <div className="form-options">
                  <button 
                    type="button" 
                    className="forgot-password"
                    onClick={handleForgotPassword}
                    disabled={loading}
                  >
                    Forgot password?
                  </button>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary btn-full"
                  disabled={loading}
                >
                  <span className="btn-content">
                    {loading ? 'Signing in...' : 'Sign In'}
                    {!loading && <FaArrowRight className="btn-icon" />}
                  </span>
                  {loading && <span className="btn-loader"></span>}
                </button>
              </form>
              
              {/* Divider */}
              <div className="divider">
                <span className="divider-text">Or continue with</span>
              </div>
              
              {/* Google Sign In */}
              <button 
                className="btn btn-outline btn-full google-btn"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <FaGoogle className="google-icon" />
                Sign in with Google
              </button>
              
              {/* Sign Up Link */}
              <div className="auth-footer">
                <p className="auth-footer-text">
                  Don't have an account?{' '}
                  <Link to="/register" className="auth-link">
                    Sign up now
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;